<template>
  <div>
    <Card>
      <div class="d-sm-flex align-center">
        <header class="flex-grow-1">
          <Link
            :href="websiteLink"
            class=""
            target="_blank"
            iconSize="small"
            icon-right="$web-system-external-outline"
            :text="site.domain"
            :ga="gEvents.acessarSite"
            color="dark"
          />
          <div class="mt-2 d-md-flex gap-md align-center">
            <span class="text-overline" v-text="createAt"/>
            <Status
              dense
              iconSize="dense"
              class="mb-1"
              :value="statusWebSite.value"
              :icon="statusWebSite.icons"
              :color="statusWebSite.color"
            />
            <Status
              dense
              iconSize="dense"
              class="mb-1"
              :value="statusDomain.value"
              :icon="statusDomain.icons"
              :color="statusDomain.color"
              v-if="statusDomain"
            />
            <Status
              dense
              iconSize="dense"
              class="mb-1"
              :value="sslStatus.value"
              :icon="sslStatus.icons"
              :color="sslStatus.color"
              v-if="sslStatus"
            />
            <Link
            :href="sitePlugin"
            class="mb-1 text-caption"
            target="_blank"
            iconSize="small"
            icon-right="$web-system-external-outline"
            :text="t$.link.pluginsSuggestion"
            :ga="gEvents.teste"
            color="primary"
            v-if="sslStatus && domainSt.status === true"
            />
          </div>
        </header>
        <div class="d-flex align-center" v-if="activeActions">
          <Button outlined color="primary" :href="domainUrl" target="_blank" :label="t$.actions.manage" :ga="gEvents.editarNovoSite"/>
          <Menu :items="menuOptions">
            <template v-slot:activator="{ on, attrs }">
              <Button class="ml-2" icon="$dots-three-vertical" :title="t$.actions.menu" :disabled="migrationInProgress" :ga="gEvents.opcoesDoSite" v-bind="attrs" v-on="on"/>
            </template>
          </Menu>
        </div>
      </div>
      <Accordion :items="itemsAccordion" class="mt-5 font-weight-medium" v-if="itemsAccordion"/>
    </Card>
    <ModalSsl v-if="displaySsl" :site="site" @closeSsl="closeSsl" @enableSsl="enableSsl"/>
  </div>
</template>

<script>
import { Button, Card, Menu, Status, useLocale, useRenamePath, Accordion, Link, useGEvents } from '@locaweb/design-system-vue'
import { findDomainStatus, findSecurity, checkMigration } from '@/views/site/api/site.api'
import { getCurrentInstance, ref, onMounted } from 'vue'
import { siteStatus } from '@/functions/SiteStatus'
import { domainStatus } from '@/functions/domainStatus'
import { sslStatus } from '@/functions/sslStatus'
import { SitesRoutesPath } from '@/views/site/router'
import ModalSsl from './modal/ModalSsl'
import AccordionCdn from './accordion/AccordionCdn'
import AccordionTransfer from './accordion/AccordionTransfer'
import AccordionStepper from './accordion/AccordionStepper'

export default {
  name: 'SiteCard',
  props: {
    site: {
      type: Object,
      required: true
    }
  },
  components: {
    Card,
    Button,
    Status,
    Menu,
    Accordion,
    ModalSsl,
    Link
  },
  data: () => ({
    displaySsl: false,
    domainSt: false,
    statusSsl: false
  }),
  methods: {
    async findDomain () {
      try {
        if (this.site.status !== 'activating' && this.site.status !== 'deleting' && !this.domainTransfer) {
          const statusDomain = await findDomainStatus(this.site.domain)
          this.domainSt = statusDomain.data
          if (statusDomain.data.status === false) {
            this.statusSsl = { status: false, security_wait: false }
          } else {
            this.findSsl()
          }
        }
      } catch (error) {
        console.error(error)
      }
    },
    async findSsl () {
      try {
        const statusSsl = await findSecurity(this.site.wordpress_id)
        this.statusSsl = statusSsl.data
      } catch (error) {
        console.error(error)
      }
    },
    openModalSsl () {
      this.displaySsl = true
    },
    enableSsl () {
      this.findSsl()
      this.displaySsl = false
      this.$emit('closeSsl')
    },
    closeSsl () {
      this.displaySsl = false
    },
    replaceModal () {
      this.$emit('modalValue')
    }
  },
  computed: {
    lc () {
      return useLocale('sites.list.site')
    },
    statusWebSite () {
      const status = siteStatus(this.site, this.t$)
      return status
    },
    statusDomain () {
      let status = false
      if (this.domainSt && !this.site.cdn) {
        status = domainStatus(this.domainSt.status, this.t$.domainStatus)
      }
      return status
    },
    sslStatus () {
      let status = false
      if (this.statusSsl && !this.site.cdn) {
        status = sslStatus(this.statusSsl, this.t$.ssl)
      }
      return status
    },
    activeActions () {
      const actions = this.site.status !== 'activating' && this.site.status !== 'deleting'
      return actions
    },
    domainTransfer () {
      const domain = this.site.domain?.toLowerCase().includes(process.env.VUE_APP_WORDPRESS_ALTERNATIVE_DOMAIN)
      return domain
    },
    itemsAccordion () {
      let item = []
      if (
        this.domainSt.status === false ||
        this.statusSsl.status === false ||
        this.statusSsl.security_wait
      ) {
        item = [
          {
            label: 'Vamos terminar de configurar seu site?',
            content: AccordionStepper,
            props: {
              site: this.site,
              sslStatus: this.statusSsl,
              domainStatus: this.statusDomain.status
            },
            events: {
              openModalSsl: this.openModalSsl
            }
          }
        ]
      }
      if (this.site.cdn) {
        item = [
          {
            label: 'Saiba mais sobre o CDN',
            content: AccordionCdn
          }
        ]
      }
      if (this.domainTransfer) {
        item = [
          {
            label: 'Veja como terminar a transferência do seu site',
            content: AccordionTransfer,
            props: {
              site: this.site
            }
          }
        ]
      }
      return item
    },
    menuOptions () {
      const menuKeys = Object.keys(this.t$.menu)
      const menuItems = menuKeys.map((key) => {
        let title = this.t$.menu[key]
        let status = false
        let menu = {}
        if (key === 'cdn') {
          title = this.t$.menu.cdn[this.site.cdn]
        }
        if (this.domainTransfer && (key === 'cdn' || key === 'email')) {
          status = true
        }
        if (SitesRoutesPath[key]) {
          menu = {
            title,
            appendIcon: '$arrows-chevron-right-outline',
            to: SitesRoutesPath[key].path.replace(':id', this.site.id),
            click: () => this.replaceModal(key),
            disabled: status
          }
        } else {
          menu = {
            title,
            appendIcon: '$arrows-chevron-right-outline',
            href: process.env.VUE_APP_EMAIL_PANEL_URL,
            target: 'blank',
            disabled: status
          }
        }

        return menu
      })

      return menuItems
    },
    websiteLink () {
      const protocol = this.sslStatus ? 'https' : 'http'
      return `${protocol}://${this.site.domain}`
    }
  },
  mounted () {
    this.findDomain()
  },
  setup (props) {
    const gEvents = useGEvents('listaDeSites')
    const { proxy: { $filters } } = getCurrentInstance()
    const t$ = useLocale('sites.list.site')
    const createAt = t$.createAt.replaceValues({ date: $filters.dateFormat(props.site.created_at, 'short') })

    const domainUrl = `https://${props.site.domain}/wp-admin/`

    const sitePlugin = SitesRoutesPath.plugins.path.replace(':id', props.site.id)
    const { addPathName } = useRenamePath()
    addPathName({
      path: sitePlugin
    })

    const migrationInProgress = ref(false)

    const checkMigrationStatus = async () => {
      try {
        const result = await checkMigration(props.site.plan_id)
        migrationInProgress.value = !!result
      } catch (error) {
        console.error('Error checking migration:', error)
      }
    }

    onMounted(checkMigrationStatus)

    return {
      t$,
      createAt,
      domainUrl,
      gEvents,
      sitePlugin,
      migrationInProgress
    }
  }
}
</script>
